// Story 8.7 (ADO-2016) — Toast. Figma "Toast - Item Added to Basket"; the Previous Orders
// re-add instances are 5943:31353 ("Order added to cart") and 5943:31545 ("Cart replaced"),
// and the add-to-cart instances are 4002:14600 / 4002:14998 ("Added to cart",
// `Qty {n} · {product}`, Story 7.9, ADO-1141).
//
// A dark pill: the success CheckCircle, a title over supporting text, and the ShoppingCart.
// By default the pill hugs its text and the rows wrap, so text that must stay whole (a re-add
// toast's order total) is never cut off. The `addedToCart` kind (the add-to-cart instances)
// is a fixed 360px pill, narrower only when its host is: the title keeps to one line and the
// description wraps to at most two, each ending in an ellipsis when it runs out of room. The
// full text stays in the DOM for the live region to announce.
//
// PRESENTATIONAL: it neither positions itself nor times itself out. The app-wide `ToastHost`
// (`apps/web/src/toast`) places it, owns the polite live region it is announced through, and
// dismisses it; the camera scanner, an aria-modal dialog, does the same for its own copy
// inside the dialog (`app/Scan/ScanModal.tsx`).
//
// ADO-2262: `copyKey` keys the title and description block on its own and marks it
// `data-part="copy"`, so a host can replace the copy inside a toast that stays on screen (the
// new copy is new content in the live region, so it is announced). Without it the toast draws
// exactly as before.
//
// Pricing migration 32 (ADO-3985), R3 21w and 21s: the warning tone (the triangle, no cart icon),
// the Action option (one text link, white and underlined on the dark fill, the Toast note) and ✕
// for a toast that stays until it is dismissed or its action is used (WCAG 2.2.1).

import { Icon } from '../Icon/Icon';
import styles from './Toast.module.css';

/** Story 7.9 (ADO-1141): which toast this is, where a kind draws differently from the default
 *  hugging pill. `addedToCart` (Figma 4002:14998) is the fixed 360px pill with a one-line
 *  title and a description of at most two lines, each ending in an ellipsis. The kind sets
 *  only the pill's shape: the host places every toast the same way. */
export type ToastKind = 'addedToCart';

/** ADO-3985 (R3 21w, 21s): `warning` draws the warning triangle and no cart icon. */
export type ToastTone = 'success' | 'warning';

/** ADO-3985: the Toast's Action option, one text link (the Toast note: "e.g. "Add 1 case""). */
export interface ToastAction {
  readonly label: string;
  readonly onAction: () => void;
}

export interface ToastProps {
  readonly title: string;
  readonly description: string;
  readonly kind?: ToastKind | undefined;
  /** ADO-2262: keys the copy block alone; a new key replaces the copy, not the toast. */
  readonly copyKey?: string | number | undefined;
  readonly tone?: ToastTone | undefined;
  readonly action?: ToastAction | undefined;
  /** Draws ✕ ("Dismiss", as the Banner names it), for a toast that stays until dismissed. */
  readonly onDismiss?: (() => void) | undefined;
}

export function Toast({
  title,
  description,
  kind,
  copyKey,
  tone = 'success',
  action,
  onDismiss,
}: ToastProps) {
  const interactive = action !== undefined || onDismiss !== undefined;
  return (
    <div
      className={kind === 'addedToCart' ? `${styles.toast} ${styles.fixedWidth}` : styles.toast}
      data-tone={tone}
      data-interactive={interactive ? '' : undefined}
    >
      {tone === 'warning' ? (
        <Icon name="warning" size={20} className={styles.warningIcon} />
      ) : (
        <Icon name="checkCircleBold" size={20} className={styles.check} />
      )}
      <div
        key={copyKey}
        className={styles.text}
        data-part={copyKey === undefined ? undefined : 'copy'}
      >
        <p className={styles.title}>{title}</p>
        {description === '' ? null : <p className={styles.description}>{description}</p>}
        {action === undefined ? null : (
          <button type="button" className={styles.action} onClick={action.onAction}>
            {action.label}
          </button>
        )}
      </div>
      {tone === 'warning' ? null : <Icon name="shoppingCart" size={18} className={styles.cart} />}
      {onDismiss === undefined ? null : (
        <button type="button" className={styles.dismiss} aria-label="Dismiss" onClick={onDismiss}>
          <Icon name="x" size={18} />
        </button>
      )}
    </div>
  );
}
